import { useEffect, useRef } from "react";

import { CbhFeatureFlag, useCbhFlag } from "../../FeatureFlags";
import { setFirebaseSessionFallbackEnabled } from "./firebaseSessionFallbackState";

/**
 * Syncs the `2026-03-disable-firebase-session-fallback` LaunchDarkly flag
 * into module-level state so that non-React code (session init, logout)
 * can read the current value.
 *
 * The first sync happens during render (not in an effect) so that child
 * component effects can read the correct flag value on the initial mount.
 * Subsequent updates happen in an effect to avoid setting state during render
 * after the initial sync.
 *
 * Mount this hook once, high in the component tree (e.g. App layout).
 */
export function useSyncFirebaseSessionFallbackFlag(): void {
  const isDisabled = useCbhFlag(CbhFeatureFlag.DISABLE_FIREBASE_SESSION_FALLBACK, {
    defaultValue: false,
  });
  const hasSyncedRef = useRef(false);

  // Sync during render on first mount so the value is available before
  // child component effects run.
  if (!hasSyncedRef.current) {
    hasSyncedRef.current = true;
    setFirebaseSessionFallbackEnabled(!isDisabled);
  }

  // Sync on subsequent flag changes via effect.
  useEffect(() => {
    setFirebaseSessionFallbackEnabled(!isDisabled);
  }, [isDisabled]);
}
